import { IProjectMetadata } from 'holberton-school-intranet-api';
import * as React from 'react';
import { ReactElement } from 'react';

import DateTime from '../common/DateTime';
import Icon from '../common/Icon';
import Tooltip from '../common/Tooltip';
import { t } from '../../utils/i18n';

interface IProps {
    metadata: IProjectMetadata;
}
export default function ProjectMetadata({ metadata }: IProps): ReactElement {
    return (
        <ul className="list-group metadata" id="project-metadata">
            {metadata.level && (
                <li className="list-group-item">
                    <Icon fixedWidth name="level-up" /> {metadata.level}
                </li>
            )}

            {metadata.author && (
                <li className="list-group-item">
                    <Icon fixedWidth name="user" /> {t('projects.metadata.by', 'By')}: {metadata.author}
                </li>
            )}

            {metadata.weight !== undefined && (
                <li className="list-group-item">
                    <Icon fixedWidth name="cog" /> {t('projects.metadata.weight', 'Weight')}: {metadata.weight}
                </li>
            )}

            {metadata.manual_review && (
                <li className="list-group-item">
                    <Icon fixedWidth name="check" /> {t('projects.metadata.manual_review_behavior', 'Manual review behavior')}:{' '}
                    {metadata.manual_review.behavior}{' '}
                    <Tooltip title={metadata.manual_review.description}>
                        <Icon name="question-circle" />
                    </Tooltip>
                </li>
            )}

            {metadata.correction?.synchronous_manual_review !== undefined && (
                <li className="list-group-item">
                    <Icon fixedWidth name="toggle-on" />
                    {metadata.correction.synchronous_manual_review ? (
                        <>
                            {' '}
                            {t('projects.metadata.synchronous_manual_review', 'Synchronous manual review')}{' '}
                            <Tooltip title={t('projects.metadata.synchronous_tooltip', 'Synchronous (Interview mode): Student and reviewer need to review the project together.')}>
                                <Icon name="question-circle" />
                            </Tooltip>
                        </>
                    ) : (
                        <> {t('projects.metadata.asynchronous_manual_review', 'Asynchronous manual review')}</>
                    )}
                </li>
            )}

            {metadata.team && (
                <li className="list-group-item">
                    <Icon fixedWidth name="users" /> {t('projects.metadata.project_to_be_done_in_teams', 'Project to be done in teams of').replace('%{count}', String(metadata.team.in_team_of))} {metadata.team.in_team_of} {t('projects.metadata.to', 'people')}
                    {metadata.team.members && (
                        <> ({t('projects.metadata.your_team', 'your team').replace('%{members}', '')} {metadata.team.members.join(', ')})</>
                    )}
                </li>
            )}

            {metadata.task_level_review_type && (
                <li className="list-group-item">
                    <Icon fixedWidth name="check-square" />{' '}
                    {metadata.task_level_review_type}
                </li>
            )}

            {metadata.bpi && (
                <li className="list-group-item">
                    <Icon fixedWidth name="calendar" />{' '}
                    {metadata.bpi.current ? (
                        <>
                            {metadata.bpi.started ? (
                                <>
                                    Ongoing project - started{' '}
                                    <DateTime value={metadata.bpi.starts_at} />
                                    , must end by
                                    <DateTime value={metadata.bpi.ends_at} />
                                </>
                            ) : (
                                <>
                                    Project will start{' '}
                                    <DateTime value={metadata.bpi.starts_at} />,
                                    must end by{' '}
                                    <DateTime value={metadata.bpi.ends_at} />
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            {metadata.bpi.in_second_deadline ? (
                                <>
                                    Ongoing second chance project - started{' '}
                                    <DateTime value={metadata.bpi.starts_at} />,
                                    must end by{' '}
                                    <DateTime
                                        value={metadata.bpi.second_deadline_at}
                                    />
                                </>
                            ) : (
                                <>
                                    Project over - took place from{' '}
                                    <DateTime value={metadata.bpi.starts_at} />{' '}
                                    to <DateTime value={metadata.bpi.ends_at} />
                                </>
                            )}
                        </>
                    )}
                    {metadata.bpi.completion !== undefined && (
                        <>
                            - you{"'"}re done with{' '}
                            <span id="student_task_done_percentage">
                                {metadata.bpi.completion}
                            </span>
                            % of tasks.
                        </>
                    )}
                </li>
            )}

            {metadata.correction?.auto_correction_available_at && (
                <li className="list-group-item">
                    <Icon fixedWidth name="check" />{' '}
                    {metadata.correction.released ? 'was ' : 'will be '}
                    released at{' '}
                    <DateTime
                        value={metadata.correction.auto_correction_available_at}
                    />
                </li>
            )}

            {metadata.correction?.requires_manual_correction && (
                <li className="list-group-item">
                    <Icon fixedWidth name="check-square" />
                    {metadata.correction.manual_done_at ? (
                        <>
                            {' '}
                            {t('projects.metadata.manual_qa_review_done', 'Manual QA review was done by')}{' '}
                            {metadata.correction.manual_done_by} {t('projects.metadata.on', 'on')}{' '}
                            <DateTime
                                value={metadata.correction.manual_done_at}
                            />
                        </>
                    ) : (
                        <>
                            {' '}
                            <strong>{t('projects.metadata.manual_qa_review_must_be_done', 'Manual QA review must be done')}</strong>{' '}
                            {t('projects.metadata.request_when_done', '(request it when you are done with the project)')}
                        </>
                    )}
                </li>
            )}

            {metadata.correction?.synchronous_manual_review && (
                <li className="list-group-item">
                    <Icon fixedWidth name="toggle-on" />{' '}
                    <strong>{t('projects.metadata.synchronous_manual_review_note', 'Synchronous manual review:')} </strong> {t('projects.metadata.you_and_reviewer_together', 'you and the reviewer need to review the project together')}
                </li>
            )}

            {metadata.correction?.requires_auto_correction && (
                <li className="list-group-item">
                    <Icon fixedWidth name="check-square" /> {t('projects.metadata.auto_review_launched_deadline', 'An auto review will be launched at the deadline')}
                </li>
            )}
        </ul>
    );
}
